export const DEFAULT_COUNTRY = "IN";
export const COUNTRY_COOKIE = "userCountry";
export const LS_COUNTRY_KEY = "userLocation";
/** When the country was last confirmed against the edge, so repeat visits skip the request. */
export const LS_CHECKED_KEY = "userLocationCheckedAt";

const ISO2 = /^[A-Z]{2}$/;

/**
 * Fired when CountryProbe resolves a country and writes the cookie.
 *
 * The country is resolved over the network (a same-origin call to
 * /api/country, which reads the CDN edge's IP geolocation), so it usually
 * lands after the feed has already mounted and read an empty cookie. A
 * mount-time read alone would therefore miss the visitor's first session. This
 * event closes that gap without the full page reload the code used to do.
 *
 * It is also the seam a country picker would plug into: writing the cookie and
 * dispatching this is the whole contract for changing the feed in place.
 */
export const COUNTRY_CHANGE_EVENT = "sticko:country";

/**
 * Read the resolved country on the client.
 *
 * Server components deliberately do NOT read this cookie during render any
 * more: `cookies()` anywhere in the tree opts the route out of static
 * rendering, which is what kept the six feed routes uncached. The server now
 * renders the DEFAULT_COUNTRY feed — the same response a first-time visitor
 * has always received — and LocalizedFeed swaps in the visitor's own feed
 * after hydration when this returns something different.
 *
 * Returns null when the cookie is absent or malformed. That is still the state
 * on a first paint — CountryProbe writes the cookie only once /api/country has
 * answered — and it is the permanent state for crawlers, which that route
 * answers `null` on purpose so the DOM they index matches the prerendered HTML.
 */
export function readCountryCookie(): string | null {
  if (typeof document === "undefined") return null;
  const prefix = `${COUNTRY_COOKIE}=`;
  const match = document.cookie.split("; ").find((row) => row.startsWith(prefix));
  if (!match) return null;
  const value = decodeURIComponent(match.slice(prefix.length)).toUpperCase();
  return ISO2.test(value) ? value : null;
}
